
  "use client"
  import AsyncSelect from "react-select/async"

  import { useState, useCallback, useRef, useEffect, useMemo } from "react";
  import { components } from "react-select";
  import { Waypoint } from "react-waypoint";

  const AsyncSelectDropdown = ({
    options = [],
    value,
    onChange,
    placeholder,
    isMulti = false,
    loadOptions: loadOptionsProp,
    defaultOptions,
    onMenuScrollToBottom,
    onMenuOpen: onMenuOpenProp,
    onMenuClose: onMenuCloseProp,
    pageSize = 10,
    debounceMs = 500,
    minSearchLength = 0,
    ...rest
  }) => {
    const [internalOptions, setInternalOptions] = useState([]);
    const [internalPage, setInternalPage] = useState(1);
    const [internalSearch, setInternalSearch] = useState("");
    const [internalHasMore, setInternalHasMore] = useState(true);
    const [internalLoadingMore, setInternalLoadingMore] = useState(false);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const isSelfPaginated = Boolean(loadOptionsProp) && !onMenuScrollToBottom;
    const menuListElRef = useRef(null);
    const menuScrollTopRef = useRef(0);
    const resultsCacheRef = useRef(new Map());

    // Function to load options asynchronously
    const loadOptionsLocal = (inputValue) => {
      return new Promise((resolve) => {
        const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
        resolve(filteredOptions)
      })
    }

    const resolveOptionsResult = (result) => {
      if (Array.isArray(result)) return { options: result, hasNext: null };
      if (result && typeof result === "object") {
        const opts = Array.isArray(result.options) ? result.options : [];
        const hasNext = typeof result.hasNext === "boolean" ? result.hasNext : null;
        return { options: opts, hasNext };
      }
      return { options: [], hasNext: null };
    };

    const fetchInternalPage = useCallback(async ({ search, page, append }) => {
      const result = await loadOptionsProp({ search, page, limit: pageSize });
      const { options: nextOpts, hasNext } = resolveOptionsResult(result);
      const hasMore =
        hasNext != null ? hasNext : nextOpts.length >= pageSize;
      setInternalHasMore(Boolean(hasMore));
      setInternalPage(page);
      setInternalSearch(search);
      setInternalOptions((prev) => (append ? [...prev, ...nextOpts] : nextOpts));
      return nextOpts;
    }, [loadOptionsProp, pageSize]);

    const fetchInternalPageRef = useRef(fetchInternalPage);
    useEffect(() => {
      fetchInternalPageRef.current = fetchInternalPage;
    }, [fetchInternalPage]);

    /** Must stay stable across pagination updates or react-select remounts MenuList and scroll jumps to top */
    const pagStateRef = useRef({
      menuIsOpen: false,
      internalHasMore: true,
      internalSearch: "",
      internalPage: 1,
    });
    const loadMoreInFlightRef = useRef(false);

    pagStateRef.current.menuIsOpen = menuIsOpen;
    pagStateRef.current.internalHasMore = internalHasMore;
    pagStateRef.current.internalSearch = internalSearch;
    pagStateRef.current.internalPage = internalPage;

    // Debounce wrapper
    const debounceTimeout = useRef(null);
    const emptyFetchInFlightRef = useRef(false);
    const debouncedLoadOptions = (inputValue, callback) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      const run = async () => {
        const term = String(inputValue || "");
        if (term.length > 0 && Number(minSearchLength) > 0 && term.length < Number(minSearchLength)) {
          if (isSelfPaginated) {
            setInternalHasMore(false);
            setInternalPage(1);
            setInternalSearch(term);
            setInternalOptions([]);
          }
          callback([]);
          return;
        }
        if (isSelfPaginated) {
          const search = term;
          setInternalLoadingMore(true);
          try {
            const next = await fetchInternalPage({ search, page: 1, append: false });
            callback(next);
          } finally {
            setInternalLoadingMore(false);
          }
          return;
        }
        if (term === "") {
          resultsCacheRef.current.delete("");
          const results = await (loadOptionsProp ? loadOptionsProp("") : loadOptionsLocal(""));
          callback(Array.isArray(results) ? results : []);
          return;
        }
        const cacheKey = term;
        if (resultsCacheRef.current.has(cacheKey)) {
          callback(resultsCacheRef.current.get(cacheKey));
          return;
        }
        const results = await (loadOptionsProp ? loadOptionsProp(term) : loadOptionsLocal(term));
        if (Array.isArray(results) && term !== "") {
          resultsCacheRef.current.set(cacheKey, results);
        }
        callback(results);
      };

      const termNow = String(inputValue || "");
      if (isSelfPaginated && termNow === "") {
        if (emptyFetchInFlightRef.current) return;
        emptyFetchInFlightRef.current = true;
        Promise.resolve(run()).finally(() => {
          emptyFetchInFlightRef.current = false;
        });
        return;
      }

      debounceTimeout.current = setTimeout(run, Number(debounceMs) > 0 ? Number(debounceMs) : 500);
    };

    const resolvedDefaultOptions = typeof defaultOptions !== "undefined" ? defaultOptions : options
    const resolvedOptions = isSelfPaginated ? internalOptions : resolvedDefaultOptions
    const resolvedOptionsCount = Array.isArray(resolvedOptions) ? resolvedOptions.length : 0

    useEffect(() => {
      if (!menuIsOpen) return
      const el = menuListElRef.current
      if (!el) return
      const top = menuScrollTopRef.current
      if (typeof top === "number" && top >= 0) {
        el.scrollTop = top
      }
    }, [menuIsOpen, resolvedOptionsCount]);

    // Custom MenuList: stable reference — do not depend on internalPage/hasMore/loading or scroll resets on each load
    const MenuList = useCallback(
      (props) => {
        const innerRef = (el) => {
          menuListElRef.current = el
          if (typeof props.innerRef === "function") props.innerRef(el)
          else if (props.innerRef && typeof props.innerRef === "object") props.innerRef.current = el
        }
        const onScroll = (e) => {
          const target = e?.target
          if (target && typeof target.scrollTop === "number") {
            menuScrollTopRef.current = target.scrollTop
          }
          if (props?.innerProps?.onScroll) props.innerProps.onScroll(e)
        }
        return (
          <components.MenuList
            {...props}
            innerRef={innerRef}
            innerProps={{ ...(props.innerProps || {}), onScroll }}
          >
            {props.children}
            {(onMenuScrollToBottom || isSelfPaginated) && (
              <Waypoint
                onEnter={() => {
                  const s = pagStateRef.current;
                  if (!s.menuIsOpen) return;
                  if (onMenuScrollToBottom) {
                    if (menuListElRef.current) menuScrollTopRef.current = menuListElRef.current.scrollTop;
                    onMenuScrollToBottom();
                    return;
                  }
                  if (!s.internalHasMore || loadMoreInFlightRef.current) return;
                  loadMoreInFlightRef.current = true;
                  setInternalLoadingMore(true);
                  if (menuListElRef.current) menuScrollTopRef.current = menuListElRef.current.scrollTop;
                  fetchInternalPageRef
                    .current({
                      search: s.internalSearch,
                      page: s.internalPage + 1,
                      append: true,
                    })
                    .finally(() => {
                      loadMoreInFlightRef.current = false;
                      setInternalLoadingMore(false);
                    });
                }}
              />
            )}
          </components.MenuList>
        );
      },
      [onMenuScrollToBottom, isSelfPaginated]
    );

    const selectComponents = useMemo(() => ({ MenuList }), [MenuList]);

    // Custom styles to set high z-index and dynamic theme colors
    const customStyles = {
      menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        backgroundColor: 'var(--card)',
        border: '1px solid var(--line)',
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
      /* Indigo, not --saffron. This is the app's other picker -- the
         hand-rolled one in common/NewCustomAsyncSelectDropdown reskinned
         to the rail's blue, and the two sit on the same forms, so an
         orange focus ring on one and a blue one on the other reads as a
         bug rather than a choice. Same values as --nas-accent there. */
      control: (provided, state) => ({
        ...provided,
        backgroundColor: 'var(--card)',
        borderColor: state.isFocused ? 'var(--indigo)' : 'var(--line)',
        boxShadow: state.isFocused ? '0 0 0 3px var(--indigo-soft)' : 'none',
        '&:hover': {
          borderColor: 'var(--indigo)',
        },
      }),
      singleValue: (provided) => ({
        ...provided,
        color: 'var(--ink)',
      }),
      input: (provided) => ({
        ...provided,
        color: 'var(--ink)',
      }),
      placeholder: (provided) => ({
        ...provided,
        color: 'var(--ink-3)',
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? 'var(--indigo)'
          : state.isFocused
          ? 'var(--indigo-soft)'
          : 'var(--card)',
        color: state.isSelected ? '#ffffff' : 'var(--ink)',
        '&:active': {
          backgroundColor: 'var(--indigo)',
        },
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: 'var(--paper-2)',
        border: '1px solid var(--line)',
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: 'var(--ink)',
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: 'var(--ink-2)',
        '&:hover': {
          backgroundColor: 'var(--berry)',
          color: '#ffffff',
        },
      }),
    }
    return (
      <div>
        <AsyncSelect
          isMulti={isMulti}
          loadOptions={debouncedLoadOptions}
          defaultOptions={resolvedOptions}
          options={resolvedOptions}
          value={value}
          onChange={onChange}
          placeholder={placeholder || "Select an option"}
          getOptionLabel={(e) => e.label}
          styles={customStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          components={selectComponents}
          onMenuOpen={() => {
            setMenuIsOpen(true);
            if (isSelfPaginated && internalOptions.length === 0 && !internalLoadingMore) {
              setInternalLoadingMore(true);
              fetchInternalPageRef
                .current({ search: internalSearch, page: 1, append: false })
                .finally(() => setInternalLoadingMore(false));
            }
            if (onMenuOpenProp) onMenuOpenProp();
          }}
          onMenuClose={() => {
            setMenuIsOpen(false);
            if (onMenuCloseProp) onMenuCloseProp();
          }}
          getOptionValue={(e) => e.value}
          {...rest}
        />
      </div>
    )
  }

  export default AsyncSelectDropdown
